import React, { useCallback, useEffect, useState } from 'react';
import type { InProgressIssue, IssueRestoreProps } from '../../../common/types.tsx';
import { useInProgressIssueActions } from '../../../controllers/in-progress-issue/index.tsx';
import { useIssueRestoreMetaActions } from '../../../controllers/issue-restore-meta/index.tsx';
import { useRestoreFormFields } from '../../../controllers/use-restore-form-fields/index.tsx';
import { BackgroundActions } from './background-actions/index.tsx';

type InProgressIssueInitialiserProps = IssueRestoreProps & {
	inProgressIssueID?: string;
	shouldRestore?: boolean;
};

export const InProgressIssueInitialiser = ({
	onRestoreTriggerIssueCreateModalState,
	onRestoreFormContext,
	inProgressIssueID,
	shouldRestore,
	children,
}: InProgressIssueInitialiserProps): React.JSX.Element | null => {
	const { init } = useInProgressIssueActions();
	const [isInitialised, setIsInitialised] = useState(false);
	const { setShouldRestore } = useIssueRestoreMetaActions();

	/**
	 * We are storing initialInProgressIssue in state and passing it to IssueModalWrapper to reduce re-renders
	 * so that we don't get a re-render when the InProgressIssueController state changes
	 */

	const [initialInProgressIssue, setInitialInProgressIssue] = useState<InProgressIssue>();

	const initialiseInProgressIssues = useCallback(async () => {
		if (!isInitialised) {
			const inProgressIssue = await init(inProgressIssueID);

			if (shouldRestore) {
				if (inProgressIssueID && inProgressIssue) {
					const { triggerIssueCreateModalState, formContext } = inProgressIssue;
					if (triggerIssueCreateModalState) {
						onRestoreTriggerIssueCreateModalState(triggerIssueCreateModalState);
					}
					if (formContext) {
						onRestoreFormContext(formContext);
					}
					setInitialInProgressIssue(inProgressIssue);
				}
			}
			setIsInitialised(true);
		}
	}, [
		isInitialised,
		shouldRestore,
		init,
		inProgressIssueID,
		onRestoreTriggerIssueCreateModalState,
		onRestoreFormContext,
	]);

	const setShouldRestoreForInProgressIssue = useCallback(
		(newShouldRestore: boolean) => {
			inProgressIssueID && setShouldRestore(inProgressIssueID, newShouldRestore);
		},
		[inProgressIssueID, setShouldRestore],
	);

	useEffect(() => {
		initialiseInProgressIssues();
	}, [initialiseInProgressIssues]);

	// transform create form props in order to set new default values
	const { onTransformCreateFormProps } = useRestoreFormFields(initialInProgressIssue);

	if (!isInitialised) {
		return null;
	}
	return (
		<>
			<BackgroundActions />
			{children?.({
				onTransformCreateFormProps,
				setShouldRestore: setShouldRestoreForInProgressIssue,
			})}
		</>
	);
};
